import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";

const METHODS = ["Phone", "Email", "In Person", "Meeting", "Other"];
const OUTCOMES = ["Spoke With Contact", "Left Voicemail", "No Answer", "Positive", "Needs Follow-Up", "Not Interested", "Other"];

function localNow() {
  const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export default function MonthlyContactDialog({ open, onOpenChange, businessId, businessName, contacts = [], onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ method: "Phone", contacted_at: localNow(), follow_up: false });
  }, [open]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.method) { toast.error("Contact method is required"); return; }
    if (form.follow_up && !form.follow_up_date) { toast.error("Set a follow-up date"); return; }
    setSaving(true);
    try {
      const payload = {
        method: form.method,
        contact_id: form.contact_id || null,
        contact_person: form.contact_person || null,
        outcome: form.outcome || null,
        notes: form.notes || null,
        contacted_at: form.contacted_at ? new Date(form.contacted_at).toISOString() : null,
        follow_up: !!form.follow_up,
        follow_up_date: form.follow_up ? form.follow_up_date : null,
      };
      await api.post(`/businesses/${businessId}/monthly-contact`, payload);
      toast.success("Monthly contact logged");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to log contact");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="monthly-contact-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Log Monthly Contact</DialogTitle>
          <DialogDescription>{businessName ? `Record meaningful contact with ${businessName}.` : "Record meaningful advertiser contact."}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div>
            <Label className="text-xs">Date / Time</Label>
            <Input type="datetime-local" className="mt-1" value={form.contacted_at || ""} onChange={(e) => set("contacted_at", e.target.value)} data-testid="mc-datetime" />
          </div>
          <div>
            <Label className="text-xs">Contact Method</Label>
            <Select value={form.method || "Phone"} onValueChange={(v) => set("method", v)}>
              <SelectTrigger className="mt-1" data-testid="mc-method"><SelectValue /></SelectTrigger>
              <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Contact Person</Label>
            {contacts.length > 0 ? (
              <Select value={form.contact_id || ""} onValueChange={(v) => {
                const c = contacts.find((x) => x.id === v);
                set("contact_id", v); set("contact_person", c ? `${c.first_name} ${c.last_name || ""}`.trim() : "");
              }}>
                <SelectTrigger className="mt-1" data-testid="mc-contact"><SelectValue placeholder="Select contact…" /></SelectTrigger>
                <SelectContent>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <Input className="mt-1" value={form.contact_person || ""} onChange={(e) => set("contact_person", e.target.value)} placeholder="Name" data-testid="mc-contact-text" />
            )}
          </div>
          <div>
            <Label className="text-xs">Outcome</Label>
            <Select value={form.outcome || ""} onValueChange={(v) => set("outcome", v)}>
              <SelectTrigger className="mt-1" data-testid="mc-outcome"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>{OUTCOMES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Notes</Label>
            <Textarea className="mt-1" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="mc-notes" />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={!!form.follow_up} onCheckedChange={(v) => set("follow_up", v)} data-testid="mc-followup" />
            <Label className="text-xs">Follow-Up Needed</Label>
          </div>
          {form.follow_up && (
            <div>
              <Label className="text-xs">Follow-Up Date</Label>
              <Input type="date" className="mt-1" value={form.follow_up_date || ""} onChange={(e) => set("follow_up_date", e.target.value)} data-testid="mc-followup-date" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="mc-save">{saving ? "Saving…" : "Log Contact"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
